import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";

import classNames from "classnames";
import { capitalize } from "lodash";

import { useTournamentRegistrationsContext } from "../contexts/TournamentRegistrations";

const Filters = (): React.JSX.Element => {
    const { filters, setFilters, defaultStatusFilters } =
        useTournamentRegistrationsContext();

    return (
        <Row className="registrationFilters" data-testid="registrationFilters">
            <Col xs={12} sm={6} lg={4} xl={6} className="my-2">
                <Form.Group controlId="search">
                    <Form.Label className={classNames("h4")}>
                        Search:
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Search value..."
                        value={filters.search}
                        onChange={e => {
                            setFilters(prev => ({
                                ...prev,
                                search: e.target.value,
                            }));
                        }}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group controlId="ageLevel" data-testid="ageLevelFilter">
                    <Form.Label className={classNames("h4")} id="ageLevel">
                        Age Level:
                    </Form.Label>
                    <Select
                        aria-labelledby="ageLevel"
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        value={filters.ageLevels.filter?.map(a => {
                            return {
                                value: a,
                                label: a,
                            };
                        })}
                        options={filters.ageLevels.options.map(a => {
                            return {
                                value: a,
                                label: a,
                            };
                        })}
                        onChange={a => {
                            setFilters(prev => ({
                                ...prev,
                                ageLevels: {
                                    ...prev.ageLevels,
                                    filter:
                                        a.length === 0
                                            ? undefined
                                            : a?.map(o => o.label),
                                },
                            }));
                        }}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group controlId="orderStatus" data-testid="orderStatus">
                    <Form.Label className={classNames("h4")}>
                        Order Status:
                    </Form.Label>
                    <Select
                        isSearchable={false}
                        isClearable={true}
                        isMulti={true}
                        options={filters.statuses.options.map(s => {
                            return {
                                value: s,
                                label: capitalize(s),
                            };
                        })}
                        defaultValue={filters.statuses.filter?.map(s => {
                            return s
                                ? {
                                      value: s,
                                      label: capitalize(s),
                                  }
                                : undefined;
                        })}
                        value={filters.statuses.filter?.map(o => {
                            return o
                                ? {
                                      value: o,
                                      label: capitalize(o),
                                  }
                                : undefined;
                        })}
                        onChange={s => {
                            setFilters(prev => ({
                                ...prev,
                                statuses: {
                                    ...prev.statuses,
                                    filter:
                                        s.length === 0
                                            ? defaultStatusFilters
                                            : s?.map(o => o?.value),
                                },
                            }));
                        }}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group
                    controlId="orderDateStart"
                    data-testid="orderDateStart"
                >
                    <Form.Label
                        className={classNames("h4")}
                        id="orderDateStart"
                    >
                        Order Date Start:
                    </Form.Label>
                    <ReactDatePicker
                        ariaLabelledBy="orderDateStart"
                        selected={
                            filters.orderDateStart
                                ? new Date(filters.orderDateStart)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderDateStart: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                        maxDate={filters.orderDateEnd || null}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group controlId="orderDateEnd" data-testid="orderDateEnd">
                    <Form.Label className={classNames("h4")} id="orderDateEnd">
                        Order Date End:
                    </Form.Label>
                    <ReactDatePicker
                        ariaLabelledBy="orderDateEnd"
                        selected={
                            filters.orderDateEnd
                                ? new Date(filters.orderDateEnd)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderDateEnd: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                        minDate={filters.orderDateStart || null}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group
                    controlId="completionDateStart"
                    data-testid="completionDateStart"
                >
                    <Form.Label
                        className={classNames("h4")}
                        id="completionDateStart"
                    >
                        Completion Date Start:
                    </Form.Label>
                    <ReactDatePicker
                        ariaLabelledBy="completionDateStart"
                        selected={
                            filters.orderCompletionStart
                                ? new Date(filters.orderCompletionStart)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderCompletionStart: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                        maxDate={filters.orderCompletionEnd || null}
                    />
                </Form.Group>
            </Col>
            <Col xs={12} sm={6} lg={4} xl={3} className="my-2">
                <Form.Group
                    controlId="completionDateEnd"
                    data-testid="completionDateEnd"
                >
                    <Form.Label
                        className={classNames("h4")}
                        id="completionDateEnd"
                    >
                        Completion Date End:
                    </Form.Label>
                    <ReactDatePicker
                        ariaLabelledBy="completionDateEnd"
                        selected={
                            filters.orderCompletionEnd
                                ? new Date(filters.orderCompletionEnd)
                                : undefined
                        }
                        onChange={d =>
                            setFilters(prev => ({
                                ...prev,
                                orderCompletionEnd: d || undefined,
                            }))
                        }
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                        dateFormat="MMMM d, yyyy"
                        dropdownMode="select"
                        className="form-control"
                        minDate={filters.orderCompletionStart || null}
                    />
                </Form.Group>
            </Col>
        </Row>
    );
};

export default Filters;
